import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import ErrorToast from "components/Toaster/ErrorToast";
import api from '../api'

const CourseCard = ({ course, onModuleClick }) => {
  const {
    id,
    name,
    description,
    progress,
    curent_module,
    next_module,
    timestamp,
    batch_type,
    batch_name,
    mentor_name,
  } = course;

  return (
    <Card sx={{
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      boxShadow: 'none',
      mb: 3
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography sx={{
              color: "#6b7280",
              fontSize: "14px",
              fontFamily: 'Be Vietnam Pro'
            }}>
              Started {new Date(timestamp).toLocaleDateString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {batch_type === 'cohort' && (
              <Chip
                label="Cohort"
                size="small"
                sx={{
                  backgroundColor: '#EBF5FF',
                  color: '#0172A6',
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '12px'
                }}
              />
            )}
          </Box>
        </Box>

        <Typography sx={{
          color: "#202124",
          fontSize: "24px",
          fontWeight: 500,
          fontFamily: 'Be Vietnam Pro',
          mb: 1
        }}>
          {name}
        </Typography>

        {mentor_name && (
          <Typography sx={{
            color: "#0172A6",
            fontSize: "14px",
            mb: 2,
            fontFamily: 'Be Vietnam Pro'
          }}>
            Mentor: {mentor_name}
          </Typography>
        )}

        <Typography sx={{
          color: "#6b7280",
          fontSize: "14px",
          mb: 3,
          fontFamily: 'Be Vietnam Pro',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {description}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography sx={{
            fontSize: "14px",
            color: "#6b7280",
            fontFamily: 'Be Vietnam Pro',
            mb: 1
          }}>
            Course Progress
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress || 0}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: "10px",
                backgroundColor: '#E0E0E0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#1e40af'
                }
              }}
            />
            <Typography sx={{
              color: "#6b7280",
              fontSize: "14px",
              fontFamily: 'Be Vietnam Pro',
              minWidth: '80px'
            }}>
              {`${progress || 0}% completed`}
            </Typography>
          </Box>
        </Box>

        {curent_module && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{
              color: "#6b7280",
              fontSize: "14px",
              fontFamily: 'Be Vietnam Pro',
              mb: 1
            }}>
              Current Module
            </Typography>
            <Box
              onClick={() => onModuleClick(id)}
              sx={{
                backgroundColor: "#F5F5F5",
                borderRadius: "5px",
                p: 2,
                cursor: "pointer",
                '&:hover': {
                  backgroundColor: "#EEEEEE"
                }
              }}
            >
              <Typography sx={{
                color: "#202124",
                fontSize: "14px",
                fontFamily: 'Be Vietnam Pro'
              }}>
                Module {curent_module?.module}: {curent_module?.title}
              </Typography>
            </Box>
          </Box>
        )}

        {next_module && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{
              color: "#6b7280",
              fontSize: "14px",
              fontFamily: 'Be Vietnam Pro',
              mb: 1
            }}>
              Coming Up Next
            </Typography>
            <Box
              onClick={() => onModuleClick(id)}
              sx={{
                backgroundColor: "#F5F5F5",
                borderRadius: "5px",
                p: 2,
                cursor: "pointer",
                '&:hover': {
                  backgroundColor: "#EEEEEE"
                }
              }}
            >
              <Typography sx={{
                color: "#202124",
                fontSize: "14px",
                fontFamily: 'Be Vietnam Pro'
              }}>
                Module {next_module?.module}: {next_module?.title}
              </Typography>
            </Box>
          </Box>
        )}

        <Button
          variant="contained"
          onClick={() => onModuleClick(id)}
          sx={{
            textTransform: 'none',
            fontFamily: 'Be Vietnam Pro',
            borderRadius: '5px',
            backgroundColor: '#1e40af',
            '&:hover': {
              backgroundColor: '#1e40af'
            }
          }}
        >
          Continue Learning
        </Button>
      </CardContent>
    </Card>
  );
};

const MenteeDashboardCard = () => {
  const [menteeData, setMenteeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const userProfile = useSelector((state) => state.userProfile);
  const userRole = userProfile?.data?.role;
  const accessToken = localStorage.getItem("access_token");

  const handleModuleClick = (id) => {
    navigate(`/dashboard/workspace/${id}`);
  };

  const fetchMenteeData = async () => {
    setLoading(true);
    try {
      const response = await api.get('dashboard/');
      const data = response?.data;
      // Handle both array and single object responses
      const courses = Array.isArray(data) ? data : (data ? [data] : []);
      setMenteeData(courses);
      localStorage.setItem("menteeData", JSON.stringify(courses));
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
       <ErrorToast message="Your session has expired. Please log in again."/>
      } else {
        <ErrorToast message={'Failed to fetch mentee data'}/>
      }
      setError("Failed to fetch mentee data");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === "mentee") {
      fetchMenteeData();
    }
  }, [userRole, accessToken]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: 'Be Vietnam Pro'
        }}
      >
        <Typography>{error}</Typography>
      </Box>
    );
  }

  if (!menteeData || menteeData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          fontFamily: "Be Vietnam Pro",
          textAlign: "center",
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "#f0f4f8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: 36 }}>📚</Typography>
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Be Vietnam Pro",
            fontWeight: 600,
            color: "#202124",
            mb: 1,
          }}
        >
          No Course Assigned Yet
        </Typography>
        <Typography
          sx={{
            fontFamily: "Be Vietnam Pro",
            color: "#6b7280",
            fontSize: "15px",
            maxWidth: 400,
          }}
        >
          Your mentor hasn't assigned a course to you yet. Please contact your mentor to get started with your learning journey.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <Typography variant="h5" sx={{
        mb: 3,
        fontFamily: 'Be Vietnam Pro',
        fontWeight: 600,
        color: '#202124'
      }}>
        My Enrolled Courses ({menteeData.length})
      </Typography>

      {menteeData.map((course) => (
        <CourseCard
          key={course.id}
          course={course}
          onModuleClick={handleModuleClick}
        />
      ))}
    </Grid>
  );
};

export default MenteeDashboardCard;
